import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Page, formatBRL } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Plus, Upload, Trash2, MoreVertical, Edit, ShoppingBag, PackagePlus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

const empty = { sku: "", name: "", category: "Mercearia", price: 0, cost: 0, stock: 0, min_stock: 5, lead_time_days: 7 };

export default function Catalog() {
  const [products, setProducts] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [quickAction, setQuickAction] = useState(null); // { product, type: 'sale'|'restock' }
  const [confirmAll, setConfirmAll] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [form, setForm] = useState(empty);
  const fileRef = useRef(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", form);
      toast.success("Produto adicionado");
      setOpenCreate(false); setForm(empty); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/products/${editing.id}`, {
        sku: editing.sku,
        name: editing.name,
        category: editing.category,
        price: parseFloat(editing.price),
        cost: parseFloat(editing.cost),
        stock: parseInt(editing.stock),
        min_stock: parseInt(editing.min_stock || 5),
        lead_time_days: parseInt(editing.lead_time_days || 7),
      });
      toast.success("Produto atualizado");
      setEditing(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este produto e seu histórico de vendas?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Removido");
    load();
  };

  const removeAll = async () => {
    if (deleteText !== "EXCLUIR TODOS") return;
    try {
      const r = await api.delete("/products");
      toast.success(`${r.data.deleted_products} produtos e ${r.data.deleted_sales} vendas removidos`);
      setConfirmAll(false); setDeleteText(""); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const runQuickAction = async (e) => {
    e.preventDefault();
    const qty = parseInt(quickAction.qty);
    if (!qty || qty <= 0) return toast.error("Quantidade inválida");
    try {
      const ep = quickAction.type === "sale" ? "sale" : "restock";
      const r = await api.post(`/products/${quickAction.product.id}/${ep}`, { qty });
      toast.success(`${quickAction.type === "sale" ? "Venda" : "Reposição"} registrada — estoque: ${r.data.stock}`);
      setQuickAction(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/products/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.inserted} produtos importados`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao importar");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <Page
      title="Catálogo"
      subtitle="Gerencie seus produtos. Importe via CSV, edite individualmente ou registre vendas/reposições."
      action={
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={upload} data-testid="csv-input" />
          <Button data-testid="upload-csv-btn" variant="outline" onClick={() => fileRef.current?.click()}>
            <Upload className="w-4 h-4 mr-2" /> Importar CSV
          </Button>
          <Button data-testid="delete-all-btn" variant="outline" onClick={() => setConfirmAll(true)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300">
            <Trash2 className="w-4 h-4 mr-2" /> Excluir todos
          </Button>
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button data-testid="add-product-btn" className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-2" /> Novo produto</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Novo produto</DialogTitle></DialogHeader>
              <form onSubmit={add} className="space-y-4">
                {[["sku", "SKU"], ["name", "Nome"], ["category", "Categoria"]].map(([k, l]) => (
                  <div key={k}><Label>{l}</Label><Input data-testid={`product-${k}-input`} required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-2" /></div>
                ))}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Preço</Label><Input type="number" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })} className="mt-2" /></div>
                  <div><Label>Custo</Label><Input type="number" step="0.01" required value={form.cost} onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value) })} className="mt-2" /></div>
                  <div><Label>Estoque</Label><Input type="number" required value={form.stock} onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })} className="mt-2" /></div>
                  <div><Label>Lead time (dias)</Label><Input type="number" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: parseInt(e.target.value) || 7 })} className="mt-2" /></div>
                </div>
                <DialogFooter>
                  <Button data-testid="save-product-btn" type="submit" className="bg-primary hover:bg-primary/90 w-full">Adicionar</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      <div className="text-xs text-muted-foreground bg-card border border-dashed border-border rounded-lg p-3">
        Formato CSV: <code className="text-primary">sku, name, category, price, cost, stock</code> · Para importar com histórico de vendas e planilhas modelo, acesse <a href="/app/integrations" className="text-primary hover:underline">Integrações</a>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden mt-4">
        {!products ? (
          <div className="p-6 space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : products.length === 0 ? (
          <div className="p-12 text-center">
            <div className="font-display text-lg font-medium">Nenhum produto cadastrado</div>
            <div className="text-sm text-muted-foreground mt-2">Importe um CSV ou crie manualmente para começar.</div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>SKU</TableHead>
                <TableHead>Produto</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead className="text-right">Preço</TableHead>
                <TableHead className="text-right">Custo</TableHead>
                <TableHead className="text-right">Estoque</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} data-testid={`product-${p.id}`} className="border-border">
                  <TableCell className="font-mono text-xs text-muted-foreground">{p.sku}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-muted-foreground">{p.category}</TableCell>
                  <TableCell className="text-right">{formatBRL(p.price)}</TableCell>
                  <TableCell className="text-right">{formatBRL(p.cost)}</TableCell>
                  <TableCell className="text-right font-mono">{p.stock}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button data-testid={`actions-${p.id}`} variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditing({ ...p })}><Edit className="w-4 h-4 mr-2" /> Editar</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setQuickAction({ product: p, type: "sale", qty: 1 })}><ShoppingBag className="w-4 h-4 mr-2" /> Registrar venda</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setQuickAction({ product: p, type: "restock", qty: 10 })}><PackagePlus className="w-4 h-4 mr-2" /> Reabastecer</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => remove(p.id)} className="text-red-400"><Trash2 className="w-4 h-4 mr-2" /> Excluir</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar produto</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div><Label>SKU</Label><Input data-testid="edit-sku-input" required value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} className="mt-2" /></div>
              <div><Label>Nome</Label><Input data-testid="edit-name-input" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-2" /></div>
              <div><Label>Categoria</Label><Input data-testid="edit-category-input" required value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-2" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Preço</Label><Input type="number" step="0.01" required value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="mt-2" /></div>
                <div><Label>Custo</Label><Input type="number" step="0.01" required value={editing.cost} onChange={(e) => setEditing({ ...editing, cost: e.target.value })} className="mt-2" /></div>
                <div><Label>Estoque</Label><Input data-testid="edit-stock-input" type="number" required value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="mt-2" /></div>
                <div><Label>Lead time</Label><Input type="number" value={editing.lead_time_days || 7} onChange={(e) => setEditing({ ...editing, lead_time_days: e.target.value })} className="mt-2" /></div>
              </div>
              <DialogFooter>
                <Button data-testid="edit-save-btn" type="submit" className="bg-primary hover:bg-primary/90 w-full">Salvar alterações</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick action dialog */}
      <Dialog open={!!quickAction} onOpenChange={(o) => !o && setQuickAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{quickAction?.type === "sale" ? "Registrar venda" : "Reabastecer estoque"}</DialogTitle>
            <DialogDescription>{quickAction?.product?.name} · estoque atual {quickAction?.product?.stock}</DialogDescription>
          </DialogHeader>
          {quickAction && (
            <form onSubmit={runQuickAction} className="space-y-4">
              <div>
                <Label>Quantidade</Label>
                <Input data-testid="quick-qty-input" type="number" min={1} required value={quickAction.qty} onChange={(e) => setQuickAction({ ...quickAction, qty: e.target.value })} className="mt-2 text-center text-2xl font-display h-14" autoFocus />
              </div>
              <Button data-testid="quick-confirm-btn" type="submit" className="w-full bg-primary hover:bg-primary/90">
                {quickAction.type === "sale" ? "Vender" : "Reabastecer"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete-all confirmation */}
      <Dialog open={confirmAll} onOpenChange={(o) => { if (!o) { setConfirmAll(false); setDeleteText(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" /> Excluir todos os produtos
            </DialogTitle>
            <DialogDescription>
              Esta ação não pode ser desfeita. Todos os {products?.length || 0} produtos e seu histórico de vendas serão removidos permanentemente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs text-muted-foreground">Para confirmar, digite <code className="text-red-400">EXCLUIR TODOS</code></Label>
            <Input data-testid="confirm-delete-text" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} placeholder="EXCLUIR TODOS" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmAll(false); setDeleteText(""); }} className="flex-1">Cancelar</Button>
            <Button data-testid="confirm-delete-btn" disabled={deleteText !== "EXCLUIR TODOS"} onClick={removeAll} className="flex-1 bg-red-500 hover:bg-red-600 text-white disabled:opacity-30">
              Excluir tudo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
